<template>
  <div class="login-page">
    <!-- Show wrong app redirect when user is not allowed -->
    <WrongAppRedirect v-if="showWrongAppRedirect" :user-types="wrongAppUserTypes" :redirect-url="wrongAppRedirectUrl" />

    <!-- Normal login form -->
    <div v-else class="login-shell">
      <section class="brand-panel">
        <div class="brand-top">
          <img :src="currentLogo" alt="Scope E-inspectie" class="brand-logo" />
          <span class="brand-badge">Opdrachtgeversportaal</span>
        </div>
        <div class="brand-content">
          <h1 class="brand-title">Grip op uw inspecties en rapporten</h1>
          <p class="brand-subtitle">
            Voor eigenaren, huurders en installateurs die overzicht willen over offertes, planning en rapportage.
          </p>
        </div>
        <ul class="brand-list">
          <li>
            <i class="pi pi-file-check"></i>
            <span>Offertes bekijken, accorderen en volgen</span>
          </li>
          <li>
            <i class="pi pi-chart-line"></i>
            <span>Inspectiestatus en rapporten altijd bij de hand</span>
          </li>
          <li>
            <i class="pi pi-building"></i>
            <span>Inzicht per locatie, huurder of project</span>
          </li>
        </ul>
        <div class="brand-note">
          <i class="pi pi-info-circle"></i>
          <span>Geen account? Neem contact op met uw inspectiepartner of beheerder.</span>
        </div>
      </section>

      <section class="login-panel">
        <div class="login-card">
          <div class="login-header">
            <div>
              <h2>Inloggen</h2>
              <p>Gebruik uw e-mailadres. We sturen een inloglink of een eenmalige code.</p>
            </div>
          </div>
          <div class="login-steps">
            <div class="login-step" :class="{ active: !showOtp }">
              <span class="step-number">1</span>
              <span class="step-label">E-mailadres</span>
            </div>
            <div class="login-step" :class="{ active: showOtp }">
              <span class="step-number">2</span>
              <span class="step-label">Bevestig code</span>
            </div>
          </div>
          <form @submit.prevent="handleSubmit" class="login-form">
            <div class="form-group">
              <label for="email" class="hidden">E-mailadres</label>
              <InputText id="email" v-model="email" type="email" required :disabled="authStore.loading"
                placeholder="E-mailadres" @keyup.enter="handleEmailEnter" autocomplete="email" name="email"
                inputmode="email" :spellcheck="false" fluid />
              <p class="field-hint">Gebruik het e-mailadres dat bij uw organisatie bekend is.</p>
            </div>

            <Message v-if="emailSent && !showOtp" severity="success" :closable="false">
              Er is een inloglink verstuurd naar <b>{{ email }}</b>.
            </Message>

            <Button v-if="!showOtp" type="button" @click="requestMagicLink" :loading="authStore.loading"
              label="Stuur inloglink" icon="pi pi-envelope" class="w-full" />

            <Button v-if="!showOtp" type="button" @click="requestOtp" :loading="authStore.loading"
              :label="otpRequested ? 'Verstuur code opnieuw' : 'Gebruik eenmalige code'" icon="pi pi-send"
              severity="secondary" class="w-full" />

            <!-- OTP Input Section -->
            <div v-if="showOtp" class="otp-section">
              <div class="otp-header">
                <h3>Voer de ontvangen code in</h3>
                <p>We hebben een 6-cijferige code naar uw e-mailadres gestuurd.</p>
              </div>
              <div class="otp-input-container">
                <InputOtp v-model="otp" :length="6" integerOnly />
              </div>
              <div class="otp-actions">
                <Button :loading="authStore.loading" label="Verifieer" icon="pi pi-check" severity="success"
                  @click="verifyOtp" class="w-full" />
                <Button label="Code niet ontvangen?" severity="secondary" @click="requestOtp" icon="pi pi-refresh" text
                  class="mt-2 w-full" />
              </div>
            </div>

            <div ref="messagesContainer" class="login-messages">
              <Message v-if="error" severity="error" :closable="false">
                {{ error }}
                <div v-if="detailedError" class="error-details">
                  Details: {{ detailedError }}
                </div>
              </Message>
            </div>
          </form>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, nextTick } from "vue";
  import { useAuthStore } from "../stores/auth";
  import { useThemeStore } from "../stores/theme";
  import { useRouter, useRoute } from "vue-router";
  import InputText from "primevue/inputtext";
  import Button from "primevue/button";
  import Message from "primevue/message";
  import InputOtp from "primevue/inputotp";
  import type { UserType } from "../composables/useUserTypeCheck";
  import WrongAppRedirect from "../components/WrongAppRedirect.vue";
  import { supabase } from "../lib/supabase";
  import {
    clearStoredPostLoginRedirect,
    getStoredPostLoginRedirect,
    resolvePortalRouteForUser,
    sanitizeEmailPrefill,
    sanitizeRedirectTarget,
    storePostLoginRedirect,
  } from "../utils/authNavigation";
  import logoLight from "../assets/logoLight.svg";
  import logoDark from "../assets/logoDark.svg";

  const router = useRouter();
  const route = useRoute();
  const authStore = useAuthStore();
  const themeStore = useThemeStore();
  const currentLogo = computed(() => themeStore.isDark ? logoDark : logoLight);

  // Capture the redirect URL when component mounts to prevent loss during OTP flow
  const capturedRedirect = ref(
    sanitizeRedirectTarget(
      typeof route.query.redirect === 'string'
        ? route.query.redirect
        : getStoredPostLoginRedirect() || '/'
    )
  );

  // Get the redirect URL from query params, default to home
  const redirectUrl = computed(() => {
    // Use captured redirect if available, otherwise fall back to current route query
    return capturedRedirect.value || sanitizeRedirectTarget(typeof route.query.redirect === 'string' ? route.query.redirect : '/');
  });

  const email = ref(sanitizeEmailPrefill(typeof route.query.email === 'string' ? route.query.email : ''));
  const error = ref("");
  const detailedError = ref("");
  const emailSent = ref(false);
  const showOtp = ref(false);
  const otp = ref("");
  const otpRequested = ref(false);

  // Wrong app redirect state
  const showWrongAppRedirect = ref(false);
  const wrongAppUserTypes = ref<UserType[]>([]);
  const wrongAppRedirectUrl = ref('');

  function isRateLimitError(message: string) {
    return /you can only request this after \d+ seconds/i.test(message) || message.includes("over_request_rate_limit");
  }

  async function handleEmailEnter() {
    // Request the lowest-friction option first when the user confirms their email.
    if (!showOtp.value) {
      await requestMagicLink();
    }
  }

  async function handleSubmit() {
    // Prevent default form submission since we handle everything with buttons
    return;
  }

  // Check user type after successful authentication
  async function verifyUserTypeAndRedirect() {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id ?? authStore.user?.id;
    console.log('[Login] verifyUserTypeAndRedirect called, userId:', userId);
    console.log('[Login] Current route.query:', route.query);
    console.log('[Login] redirectUrl.value:', redirectUrl.value);
    if (!userId) {
      throw new Error('Authenticated session missing after OTP verification');
    }

    try {
      const destination = await resolvePortalRouteForUser(userId, redirectUrl.value);

      if (typeof destination !== 'string') {
        const typesParam = Array.isArray(destination.query?.types) ? destination.query?.types[0] : destination.query?.types;
        const redirectParam = Array.isArray(destination.query?.redirectUrl)
          ? destination.query?.redirectUrl[0]
          : destination.query?.redirectUrl;

        // Show the wrong-app screen in-place to avoid a visible route hop right after login.
        showWrongAppRedirect.value = true;
        wrongAppUserTypes.value = typeof typesParam === 'string' && typesParam.length > 0
          ? typesParam.split(',').filter(Boolean) as UserType[]
          : ['unknown'];
        wrongAppRedirectUrl.value = typeof redirectParam === 'string' ? redirectParam : '';
        clearStoredPostLoginRedirect();
        console.log('[Login] User not allowed, showing wrong app redirect');
        return;
      }

      // Wait for Vue reactivity cycle to complete (Navigation appearing after login)
      // before navigating, to prevent the out-in transition from getting stuck
      console.log('[Login] Redirecting to:', destination);
      await nextTick();
      clearStoredPostLoginRedirect();
      router.push(destination);
    } catch (err) {
      console.error('Error checking user type:', err);
      // On error, allow user through (fail open for now)
      console.log('[Login] Error, redirecting anyway to:', redirectUrl.value);
      await nextTick();
      clearStoredPostLoginRedirect();
      router.push(redirectUrl.value);
    }
  }

  async function requestOtp() {
    if (!email.value) {
      error.value = "Vul uw e-mailadres in";
      return;
    }

    // Prevent duplicate requests if already loading
    if (authStore.loading) {
      return;
    }

    error.value = "";
    detailedError.value = "";
    emailSent.value = false;

    try {
      otpRequested.value = true;
      showOtp.value = true;
      await authStore.requestOtp(email.value);
      emailSent.value = true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes("User not found") || errorMessage.includes("Signups not allowed for otp")) {
        error.value = "Dit e-mailadres is niet bekend. Alleen bestaande gebruikers kunnen inloggen.";
      } else if (isRateLimitError(errorMessage)) {
        error.value = "Er is zojuist al een code aangevraagd. Wacht een paar seconden en probeer het opnieuw.";
        detailedError.value = "";
        return;
      } else {
        error.value = "Er is een fout opgetreden bij het versturen van de inlogcode.";
      }
      detailedError.value = errorMessage;
      console.error("OTP request error:", {
        error: err,
        state: {
          email: email.value,
          authStoreError: authStore.error,
        },
      });
    }
  }

  async function requestMagicLink() {
    const normalizedEmail = sanitizeEmailPrefill(email.value);
    if (!normalizedEmail) {
      error.value = "Vul een geldig e-mailadres in";
      return;
    }

    if (authStore.loading) {
      return;
    }

    error.value = "";
    detailedError.value = "";

    try {
      storePostLoginRedirect(redirectUrl.value);
      await authStore.signInWithMagicLink(normalizedEmail, redirectUrl.value);
      showOtp.value = false;
      otpRequested.value = false;
      emailSent.value = true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (isRateLimitError(errorMessage)) {
        error.value = "Er is zojuist al een inloglink aangevraagd. Wacht een paar seconden en probeer het opnieuw.";
        detailedError.value = "";
        return;
      }
      error.value = "Er is een fout opgetreden bij het versturen van de inloglink.";
      detailedError.value = errorMessage;
      console.error("Magic link request error:", {
        error: err,
        state: {
          email: normalizedEmail,
          authStoreError: authStore.error,
        },
      });
    }
  }

  async function verifyOtp() {
    if (!otp.value) {
      error.value = "Vul de OTP code in";
      return;
    }

    error.value = "";
    detailedError.value = "";

    try {
      await authStore.loginWithOtp(email.value, otp.value);
      if (!authStore.error) {
        await verifyUserTypeAndRedirect();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (/token has expired or is invalid/i.test(errorMessage)) {
        error.value = "Deze code is verlopen of al gebruikt. Vraag een nieuwe code aan.";
        detailedError.value = "";
        otp.value = "";
        return;
      }
      error.value = "Ongeldige code. Probeer het opnieuw.";
      detailedError.value = errorMessage;
      console.error("OTP verification error:", {
        error: err,
        state: {
          email: email.value,
          otp: otp.value,
          authStoreError: authStore.error,
        },
      });
    }
  }
</script>

<style scoped>
  .login-page {
    min-height: 100vh;
    padding: 2.5rem 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: none;
    margin: 0;
    background:
      radial-gradient(circle at 20% 20%, var(--primary-alpha-10), transparent 55%),
      radial-gradient(circle at 80% 80%, var(--color-warning-light), transparent 50%),
      linear-gradient(120deg, var(--p-background-0), var(--p-background-50));
    position: relative;
    overflow: hidden;
  }

  .login-page::after {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 320px;
    height: 320px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.6), transparent 65%);
    opacity: 0.35;
    pointer-events: none;
  }

  .login-shell {
    width: 100%;
    max-width: 1100px;
    display: grid;
    grid-template-columns: minmax(320px, 1.05fr) minmax(320px, 0.95fr);
    gap: 2rem;
    position: relative;
    z-index: 1;
  }
  
  .brand-panel {
    background: linear-gradient(150deg, var(--p-background-0), var(--p-background-100));
    border-radius: 4px;
    border: 1px solid var(--p-background-200);
    padding: 2.75rem;
    box-shadow: var(--shadow-sm);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    animation: rise-in 0.6s ease both;
    transition: box-shadow 0.3s ease;
  }

  .brand-panel:hover {
    box-shadow: var(--shadow-2xl);
  }

  .brand-top {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .brand-logo {
    height: 36px;
    width: auto;
  }

  .brand-badge {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
    color: var(--p-primary-700);
    background: var(--primary-alpha-10);
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
  }

  .brand-title {
    font-size: 2.25rem;
    line-height: 1.15;
    margin: 0;
    color: var(--p-background-800);
  }

  .brand-subtitle {
    margin: 0.5rem 0 0;
    font-size: 1rem;
    line-height: 1.65;
    color: var(--p-background-600);
    max-width: 26rem;
  }

  .brand-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.75rem;
  }

  .brand-list li {
    display: grid;
    grid-template-columns: 1.75rem 1fr;
    align-items: center;
    gap: 0.75rem;
    color: var(--p-background-500);
    font-size: 0.95rem;
  }

  .brand-list i {
    color: var(--p-primary-500);
    font-size: 1.1rem;
  }

  .brand-note {
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    border-radius: 8px;
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    color: var(--p-background-500);
    font-size: 0.75rem;
  }

  .brand-note i {
    color: var(--p-primary-500);
    font-size: 1.1rem;
  }

  .login-panel {
    display: flex;
    align-items: center;
  }

  .login-card {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 4px;
    padding: 2.25rem;
    box-shadow: var(--shadow-sm);
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    animation: rise-in 0.6s ease both;
    animation-delay: 0.2s;
    transition: box-shadow 0.3s ease;
  }

  .login-card:hover {
    box-shadow: var(--shadow-2xl);
  }

  .login-header h2 {
    margin: 0;
    font-size: 1.75rem;
    color: var(--p-background-900);
  }

  .login-header p {
    margin: 0.5rem 0 0;
    color: var(--p-background-600);
    font-size: 0.95rem;
  }

  .login-steps {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .login-step {
    border-radius: 999px;
    padding: 0.4rem 0.75rem;
    border: 1px dashed var(--p-background-200);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--p-text-500);
    background: var(--p-background-50);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .login-step.active {
    border-style: solid;
    color: var(--p-primary-700);
    background: var(--p-primary-50);
  }

  .step-number {
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--p-background-200);
    color: var(--p-background-800);
    font-size: 0.7rem;
  }

  .login-step.active .step-number {
    background: var(--p-primary-500);
    color: var(--p-primary-50);
  }

  .login-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  label {
    font-weight: 600;
    color: var(--p-text-600);
    font-size: 0.875rem;
  }

  .field-hint {
    margin: 0.25rem 0 0;
    font-size: 0.8rem;
    color: var(--p-text-500);
  }

  /* OTP Section Styles */
  .otp-section {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
    padding: 1rem 1.25rem;
    border-radius: 8px;
    background: var(--p-background-50);
    border: 1px solid var(--p-background-200);
  }

  .otp-header h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--p-background-800);
  }

  .otp-header p {
    margin: 0;
    font-size: 0.875rem;
    color: var(--p-background-600);
  }

  .otp-input-container {
    display: flex;
    justify-content: center;
    margin: 0.5rem 0 0;
  }

  .otp-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .login-messages :deep(.p-message) {
    margin: 0.5rem 0 0;
  }

  :deep(.p-inputotp-input) {
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 12px;
  }

  /* Utility classes for PrimeVue components */
  .w-full {
    width: 100%;
  }

  .mt-2 {
    margin-top: 0.5rem;
  }

  /* Error details styling */
  .error-details {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 0.5rem;
  }

  @keyframes rise-in {
    from {
      opacity: 0;
      transform: translateY(16px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {

    .brand-panel,
    .login-card {
      animation: none;
    }
  }

  /* Responsive adjustments */
  @media (max-width: 960px) {
    .login-shell {
      grid-template-columns: 1fr;
    }

    .brand-panel {
      order: 2;
    }

    .login-panel {
      order: 1;
    }
  }

  @media (max-width: 640px) {
    .login-page {
      padding: 1.5rem 1rem;
    }

    .brand-panel,
    .login-card {
      padding: 1.75rem;
    }

    .brand-title {
      font-size: 1.8rem;
    }

    .login-header h2 {
      font-size: 1.5rem;
    }

    .login-steps {
      grid-template-columns: 1fr;
    }
  }
</style>
